import { motion } from 'framer-motion'
import { Shield, Lock, AlertTriangle, CheckCircle } from 'lucide-react'

const PermissionManagement = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="prose prose-invert max-w-none"
    >
      <h1 className="text-3xl font-bold mb-6 flex items-center">
        <Shield className="mr-3 text-accent-cyan" size={32} />
        权限管理
      </h1>

      {/* 权限系统介绍 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">为什么需要权限管理</h2>
        <p className="text-gray-400 mb-4">
          权限管理是 Avelia 的安全防护系统，用于控制 AI 可以访问和操作的资源。
          通过设置权限规则，你可以保护重要文件、限制危险操作，确保系统安全。
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 not-prose">
          <div className="glass-effect rounded-lg p-5">
            <Lock className="text-accent-cyan mb-3" size={28} />
            <h3 className="font-semibold mb-2 text-lg">保护隐私</h3>
            <p className="text-sm text-gray-400">
              防止 AI 访问敏感文件和个人数据。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-5">
            <AlertTriangle className="text-accent-cyan mb-3" size={28} />
            <h3 className="font-semibold mb-2 text-lg">防止误操作</h3>
            <p className="text-sm text-gray-400">
              限制危险操作，避免意外删除或修改重要文件。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-5">
            <CheckCircle className="text-accent-cyan mb-3" size={28} />
            <h3 className="font-semibold mb-2 text-lg">灵活控制</h3>
            <p className="text-sm text-gray-400">
              根据需要自定义权限规则，平衡安全与便利。
            </p>
          </div>
        </div>
      </section>

      {/* 权限策略 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">权限策略</h2>
        
        <div className="space-y-4">
          <div className="glass-effect rounded-lg p-5 border-l-4 border-green-500">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <CheckCircle className="mr-2 text-green-400" size={24} />
              允许 (Allow)
            </h3>
            <p className="text-sm text-gray-400 mb-2">
              AI 可以自由访问和操作，无需额外确认。
            </p>
            <div className="p-3 bg-green-500/10 rounded text-xs">
              <strong className="text-green-300">适用场景：</strong>
              <p className="text-gray-400 mt-1">
                工作目录、临时文件夹、公开资源等安全区域
              </p>
            </div>
          </div>

          <div className="glass-effect rounded-lg p-5 border-l-4 border-yellow-500">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <AlertTriangle className="mr-2 text-yellow-400" size={24} />
              慎重 (Caution)
            </h3>
            <p className="text-sm text-gray-400 mb-2">
              AI 可以访问，但会在执行前提示确认。
            </p>
            <div className="p-3 bg-yellow-500/10 rounded text-xs">
              <strong className="text-yellow-300">适用场景：</strong>
              <p className="text-gray-400 mt-1">
                重要文档、系统配置、需要谨慎处理的操作
              </p>
            </div>
          </div>

          <div className="glass-effect rounded-lg p-5 border-l-4 border-red-500">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Lock className="mr-2 text-red-400" size={24} />
              拒绝 (Deny)
            </h3>
            <p className="text-sm text-gray-400 mb-2">
              AI 完全无法访问，任何尝试都会被阻止。
            </p>
            <div className="p-3 bg-red-500/10 rounded text-xs">
              <strong className="text-red-300">适用场景：</strong>
              <p className="text-gray-400 mt-1">
                系统目录、密码文件、敏感数据等禁区
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 权限类型 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">权限类型</h2>
        
        <div className="glass-effect rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-3 font-semibold">类型</th>
                <th className="text-left p-3 font-semibold">说明</th>
                <th className="text-left p-3 font-semibold">示例</th>
              </tr>
            </thead>
            <tbody className="text-gray-400">
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">文件系统</td>
                <td className="p-3">控制文件和文件夹的访问</td>
                <td className="p-3 text-xs">C:/Users/Documents</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">进程</td>
                <td className="p-3">控制程序的启动和管理</td>
                <td className="p-3 text-xs">notepad.exe</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">网络</td>
                <td className="p-3">控制网络访问和连接</td>
                <td className="p-3 text-xs">192.168.1.1</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">命令执行</td>
                <td className="p-3">控制系统命令的执行</td>
                <td className="p-3 text-xs">shutdown, format</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">截图</td>
                <td className="p-3">控制屏幕截图功能</td>
                <td className="p-3 text-xs">screenshot</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">服务管理</td>
                <td className="p-3">控制系统服务的操作</td>
                <td className="p-3 text-xs">Windows Update</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 添加权限规则 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">添加权限规则</h2>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              1
            </div>
            <div>
              <h3 className="font-semibold mb-2">打开权限管理</h3>
              <p className="text-gray-400 text-sm">
                打开设置窗口，进入"权限"页面，点击"添加权限规则"。
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              2
            </div>
            <div>
              <h3 className="font-semibold mb-2">配置规则</h3>
              <ul className="text-sm text-gray-400 space-y-2 ml-4">
                <li className="flex items-start">
                  <span className="text-accent-cyan mr-2">•</span>
                  <span><strong className="text-white">目标类型：</strong>选择要控制的资源类型</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent-cyan mr-2">•</span>
                  <span><strong className="text-white">路径/标识：</strong>输入具体的路径或标识符</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent-cyan mr-2">•</span>
                  <span><strong className="text-white">匹配方式：</strong>选择包含、等于或不等于</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent-cyan mr-2">•</span>
                  <span><strong className="text-white">策略：</strong>选择允许、慎重或拒绝</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              3
            </div>
            <div>
              <h3 className="font-semibold mb-2">保存生效</h3>
              <p className="text-gray-400 text-sm">
                点击"添加"保存规则，规则会立即生效。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 规则示例 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">常用规则示例</h2>
        
        <div className="space-y-3 text-sm">
          <div className="p-4 glass-effect rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <strong className="text-white">保护系统目录</strong>
              <span className="px-2 py-1 rounded text-xs font-semibold bg-red-500/20 text-red-300 border border-red-500/30">拒绝</span>
            </div>
            <p className="text-gray-400 mb-2">
              <strong>类型：</strong>文件系统 | <strong>路径：</strong>C:/Windows | <strong>匹配：</strong>包含
            </p>
            <p className="text-xs text-gray-500">
              防止 AI 访问 Windows 系统目录
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <strong className="text-white">保护个人文档</strong>
              <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">慎重</span>
            </div>
            <p className="text-gray-400 mb-2">
              <strong>类型：</strong>文件系统 | <strong>路径：</strong>C:/Users/[用户名]/Documents | <strong>匹配：</strong>包含
            </p>
            <p className="text-xs text-gray-500">
              访问文档前需要确认
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <strong className="text-white">允许工作目录</strong>
              <span className="px-2 py-1 rounded text-xs font-semibold bg-green-500/20 text-green-300 border border-green-500/30">允许</span>
            </div>
            <p className="text-gray-400 mb-2">
              <strong>类型：</strong>文件系统 | <strong>路径：</strong>D:/Work | <strong>匹配：</strong>包含
            </p>
            <p className="text-xs text-gray-500">
              工作目录可以自由访问
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <strong className="text-white">禁止危险命令</strong>
              <span className="px-2 py-1 rounded text-xs font-semibold bg-red-500/20 text-red-300 border border-red-500/30">拒绝</span>
            </div>
            <p className="text-gray-400 mb-2">
              <strong>类型：</strong>命令执行 | <strong>路径：</strong>format | <strong>匹配：</strong>包含
            </p>
            <p className="text-xs text-gray-500">
              禁止执行格式化等危险命令
            </p>
          </div>
        </div>
      </section>

      {/* 使用建议 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">使用建议</h2>
        
        <div className="space-y-3">
          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 分层防护</h4>
            <p className="text-sm text-gray-400">
              建立多层权限规则：系统目录拒绝、重要文件慎重、工作目录允许。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 最小权限原则</h4>
            <p className="text-sm text-gray-400">
              默认采用较严格的策略，只对确实需要的资源放宽权限。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 定期审查</h4>
            <p className="text-sm text-gray-400">
              定期检查权限规则，删除不再需要的规则，更新过时的配置。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-semibold mb-2 text-yellow-300">⚠️ 平衡安全与便利</h4>
            <p className="text-sm text-gray-400">
              过于严格的权限会影响使用体验，根据实际需求合理配置。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-semibold mb-2 text-yellow-300">⚠️ 测试规则</h4>
            <p className="text-sm text-gray-400">
              添加新规则后，测试是否按预期工作，避免误拦截正常操作。
            </p>
          </div>
        </div>
      </section>

      {/* 提示 */}
      <section className="mt-8 p-6 glass-effect rounded-lg border border-accent-cyan/30">
        <h3 className="text-xl font-semibold mb-4">💡 安全提示</h3>
        <ul className="space-y-2 text-sm text-gray-400">
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">首次使用：</strong>建议先添加基本的保护规则，再根据需要调整</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">敏感操作：</strong>对于删除、格式化等危险操作，建议设置为"拒绝"或"慎重"</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">网络安全：</strong>限制对内网和敏感服务器的访问</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">备份重要规则：</strong>定期导出权限配置，防止意外丢失</span>
          </li>
        </ul>
      </section>
    </motion.div>
  )
}

export default PermissionManagement
